import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import {
  Menu, 
  X, 
  Mail, 
  Phone, 
  MapPin, 
  ChevronRight, 
  Layers, 
  Box, 
  Grid3X3, 
  Cloud, 
  Home, 
  FileText, 
  Calculator, 
  Wrench,
  Building2,
  Award,
  Clock,
  Briefcase,
  Monitor,
  Cpu,
  Zap,
  ArrowUpRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

const LOGO_URL = "/assets/images/logo.png";

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#bimforge", label: "BIMForge" },
    { href: "#architecture", label: "Architecture" },
    { href: "#portfolio", label: "Experience" },
    { href: "#case-studies", label: "Case Studies" },
    { href: "#about", label: "About" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-[#0A0C10]/95 backdrop-blur-xl border-b border-[#2C313D]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <a href="#" data-testid="logo-link" className="flex items-center">
            <img src={LOGO_URL} alt="BIMForge" className="h-10 w-auto" />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className="nav-link text-sm font-medium"
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            <a
              href="mailto:dustin@bimforge.co.nz"
              data-testid="nav-email-btn"
              className="btn-primary-blue text-sm"
            >
              Get in Touch
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 relative z-[110]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        data-testid="mobile-menu"
        className={`mobile-menu ${isMobileMenuOpen ? "open" : ""}`}
      >
        {navLinks.map((link, idx) => (
          <a
            key={link.href}
            href={link.href}
            data-testid={`mobile-nav-${link.label.toLowerCase()}`}
            className="text-2xl font-medium py-4 border-b border-[#2C313D]"
            onClick={() => setIsMobileMenuOpen(false)}
            style={{ animationDelay: `${idx * 0.1}s` }}
          >
            {link.label}
          </a>
        ))}
        <a
          href="mailto:dustin@bimforge.co.nz"
          data-testid="mobile-email-btn"
          className="btn-primary-blue text-center mt-8"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          Get in Touch
        </a>
      </div>
    </nav>
  );
};

// Hero Section with Split Layout
const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="hero-split pt-20">
      {/* BIMForge Panel - Technical/B2B */}
      <div className="hero-panel hero-panel-blue" data-testid="hero-bimforge">
        <div className="relative z-10 max-w-lg">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#3B82F6] mb-4 block">
            For Manufacturers
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-none mb-6">
            <span className="gradient-text-blue">BIMForge</span>
          </h1>
          <p className="text-lg text-[#A1A7B3] mb-8 leading-relaxed">
            Production-ready Revit content built for real-world New Zealand construction projects. Stop using generic international content that fails BIM standards.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="#bimforge"
              data-testid="hero-bimforge-cta"
              className="btn-primary-blue inline-flex items-center gap-2"
            >
              Upgrade Your BIM Content
              <ChevronRight size={18} />
            </a>
          </div>
        </div>
      </div>

      {/* BIMForge Architecture Panel - Residential */}
      <div className="hero-panel hero-panel-green" data-testid="hero-architecture">
        <div className="relative z-10 max-w-lg">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#10B981] mb-4 block">
            For Homeowners & Developers
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-none mb-6">
            <span className="gradient-text-green">Architecture</span>
          </h1>
          <p className="text-lg text-[#A1A7B3] mb-8 leading-relaxed">
            Expert residential design, planning, and compliance services. From subdivision to building consent, we navigate the complexities so you don't have to.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="#architecture"
              data-testid="hero-architecture-cta"
              className="btn-primary-green inline-flex items-center gap-2"
            >
              Start Your Project
              <ChevronRight size={18} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const stats = [
    { number: "23+", label: "Years Experience" },
    { number: "10+", label: "Years BIM/Revit" },
    { number: "100+", label: "Projects Delivered" },
  ];

  return (
    <section data-testid="stats-section" className="section-darker py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-3 gap-4">
          {stats.map((stat, idx) => (
            <div key={idx} className="stat-box" data-testid={`stat-${idx}`}>
              <div className="stat-number gradient-text-blue">{stat.number}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// BIMForge Services Section
const BIMForgeSection = () => {
  const services = [
    {
      icon: <Layers size={24} />,
      title: "2D Detail Item Revit Families",
      description: "Unlike generic content from offshore sources that might look acceptable on a PDF but creates absolute havoc when loaded into live Revit projects, these 2D Detail Item Packages provide genuine, production-ready assets tailored specifically for high-risk areas like facade detailing. The comprehensive suite utilizes individual element families that are nested directly into parametric detail component families. This parametric capability gives architects complete flexibility, automatically arraying individual components as they adjust and extend the length of their details. Built to the rigorous standards of New Zealand-based BIM managers, these tools enable architects to simply copy elements straight into their real-life projects for simpler and faster documentation. By investing in these critical 2D assets, you bypass the common industry gripe of manufacturers only supplying 3D models, ensuring your products provide the exact detailing precision required to seamlessly pass strict project scrutiny.",
    },
    {
      icon: <Box size={24} />,
      title: "Parametric Revit Families",
      description: "These smart parametric families act as the workhorse for producing comprehensive facade details and dynamic architectural models. Engineered as intelligent array systems, they automatically populate and space individual elements at the correct intervals based on simple user dimension inputs. This provides architects with complete detailing flexibility, seamlessly arraying components as the length or width of a detail is extended or adjusted. When utilized in a 3D environment, these system families can be easily configured to accommodate specific sizes, angular ends, and various base or top conditions. Modeled to an LOD 350 standard with fully embedded BIM data, these assets empower architects to reliably extract schedules for dimensions, areas, and finishes, while also integrating flawlessly into high-quality architectural visual renders.",
    },
    {
      icon: <Grid3X3 size={24} />,
      title: "Native Revit 3D Families LOD 350",
      description: "Stop fighting with \"dirty\" geometry. Our LOD 350 models are built from the ground up using native Revit tools to ensure lightweight files and data integrity. Fully parametric and ready for immediate use in scheduling, rendering, and multidisciplinary coordination.",
    },
    {
      icon: <Cloud size={24} />,
      title: "Managed BIM Hosting",
      description: "Live-linked manufacturer content with automatic updates synced to your website. Always current, always compliant.",
    },
  ];

  return (
    <section id="bimforge" data-testid="bimforge-section" className="section section-dark">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#3B82F6] mb-4 block">
            BIMForge Services
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Manufacturer Revit BIM Content That Actually Works
          </h2>
          <p className="text-lg text-[#A1A7B3] leading-relaxed">
            The majority of manufacturer-supplied BIM content completely misses the mark, focusing on polished 3D marketing objects while neglecting the critical 2D detail items architects actually need. Generic content churned out by offshore Revit "sweatshops" frequently causes absolute havoc in live project environments, failing the rigorous scrutiny of experienced BIM managers and leading to your products being substituted by competitors during the review stage. BIMForge solves this industry-wide frustration by delivering New Zealand-specific, production-ready documentation tooling. Leveraging over a decade of dedicated Revit content development for the country's leading architectural practices, we create technically sophisticated 2D and 3D assets that integrate seamlessly into real workflows. By providing content built to uncompromising standards, your products become the path of least resistance during the critical detailing phase - locking in specification decisions early and positioning your brand as genuinely architect-friendly.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {services.map((service, idx) => (
            <div
              key={idx}
              className="service-card service-card-blue"
              data-testid={`bimforge-service-${idx}`}
            >
              <div className="icon-box-blue mb-6">{service.icon}</div>
              <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
              <p className="text-[#A1A7B3] leading-relaxed">{service.description}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a
            href="mailto:dustin@bimforge.co.nz?subject=BIM%20Content%20Inquiry"
            data-testid="bimforge-contact-cta"
            className="btn-primary-blue inline-flex items-center gap-2"
          >
            Upgrade Your BIM Content
            <ArrowUpRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
};

// Architecture Services Section
const ArchitectureSection = () => {
  const serviceCategories = [
    {
      title: "Design & Planning",
      icon: <Home size={24} />,
      services: ["Subdivision & New Build", "Secondary Dwellings", "Extensions & Renovations", "Sleepouts", "Full 3D Visualization", "VR 3D Model Viewing"],
    },
    {
      title: "Interior & Kitchen Design",
      icon: <Grid3X3 size={24} />,
      services: ["Interior Design", "Kitchen Design", "Joinery Design", "Bathroom Design"],
    },
    {
      title: "Consenting & Compliance",
      icon: <FileText size={24} />,
      services: ["Building Consents", "Granny Flats / Detached Dwellings", "Resource Consents (AEE)", "Building Consent Exemptions", "PIM Applications", "Floodplain Mitigation", "Certificate of Acceptance (COA)"],
    },
    {
      title: "Project Management",
      icon: <Briefcase size={24} />,
      services: ["Project Coordination", "Technical Coordination", "Consultant Management", "Construction Oversight"],
    },
  ];

  return (
    <section id="architecture" data-testid="architecture-section" className="section section-darker">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#10B981] mb-4 block">
            BIMForge Architecture
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Residential Design & Compliance Expertise
          </h2>
          <p className="text-lg text-[#A1A7B3] leading-relaxed">
            From initial concept to building consent, we handle the complexities of residential development. Subdivision, new builds, renovations, and everything in between. Because we approach design through the lens of buildability, we prioritize practical, cost-effective solutions over unrealistic architectural agendas ensuring the final outcome is exactly what you need to get built.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {serviceCategories.map((category, idx) => (
            <div
              key={idx}
              className="service-card service-card-green"
              data-testid={`architecture-service-${idx}`}
            >
              <div className="icon-box-green mb-6">{category.icon}</div>
              <h3 className="text-lg font-semibold mb-4">{category.title}</h3>
              <ul className="space-y-2">
                {category.services.map((service, sIdx) => (
                  <li key={sIdx} className="text-sm text-[#A1A7B3] flex items-start gap-2">
                    <ChevronRight size={14} className="text-[#10B981] mt-1 flex-shrink-0" />
                    {service}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a
            href="mailto:dustin@bimforge.co.nz?subject=Architecture%20Project%20Inquiry"
            data-testid="architecture-contact-cta"
            className="btn-primary-green inline-flex items-center gap-2"
          >
            Start Your Project
            <ArrowUpRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
};

// Experience Section (Simple Bullet Points)
const ExperienceSection = () => {
  const experienceCategories = [
    {
      title: "Large Residential & Commercial",
      projects: ["Ockham Richmond & Koanga", "Clayburn Apartments", "Highbrook Carpark", "Hobbs Wharf"]
    },
    {
      title: "Medical & Institutional",
      projects: ["Specialist Rehabilitation Centre", "Hillmorton Hospital", "Mercy Hospital", "CHT Dementia Wards"]
    },
    {
      title: "Retail Rollouts",
      projects: ["Noel Leeming", "Warehouse Stationery", "Flight Centre", "Liquorland"]
    },
    {
      title: "Other Projects",
      projects: ["Tuhoe Living Village", "Palmers Planet", "ASB St Lukes", "Many Residential Projects", "Kitchens"]
    }
  ];

  return (
    <section id="portfolio" data-testid="portfolio-section" className="section section-dark">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#3B82F6] mb-4 block">
            Experience
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Project Experience
          </h2>
          <p className="text-lg text-[#A1A7B3] leading-relaxed">
            Over two decades delivering BIM excellence across healthcare, residential, commercial, and retail sectors with New Zealand's leading architectural firms including Jasmax, Klein Architects, and Ockham. Portfolio of work available upon request.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {experienceCategories.map((category, idx) => (
            <div key={idx} className="service-card service-card-blue" data-testid={`experience-category-${idx}`}>
              <h3 className="text-lg font-semibold mb-4 text-white">{category.title}</h3>
              <ul className="space-y-2">
                {category.projects.map((project, pIdx) => (
                  <li key={pIdx} className="text-sm text-[#A1A7B3] flex items-start gap-2">
                    <ChevronRight size={14} className="text-[#3B82F6] mt-1 flex-shrink-0" />
                    {project}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Case Studies Section
const CaseStudiesSection = () => {
  const [activeJppmImage, setActiveJppmImage] = useState(0);
  const [activeSpringImage, setActiveSpringImage] = useState(0);
  
  const springStImages = [
    "/assets/images/spring-1.png",
    "/assets/images/spring-2.png",
    "/assets/images/spring-3.png",
    "/assets/images/spring-4.png",
  ];
  
  const jppmImages = [
    "https://jppm.co.nz/wp-content/uploads/2023/06/onehunga1.jpg",
    "https://jppm.co.nz/wp-content/uploads/2023/06/onehunga2.jpg",
    "https://jppm.co.nz/wp-content/uploads/2023/06/onehunga3.jpg",
    "https://jppm.co.nz/wp-content/uploads/2023/06/onehunga4.jpg",
    "https://jppm.co.nz/wp-content/uploads/2023/06/onehunga5.jpg",
    "https://jppm.co.nz/wp-content/uploads/2023/06/onehunga6.jpg",
  ];

  return (
    <section id="case-studies" data-testid="case-studies-section" className="section section-darker">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#10B981] mb-4 block">
            Case Studies
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Featured Projects
          </h2>
          <p className="text-lg text-[#A1A7B3] leading-relaxed">
            Detailed examples of our architectural design and documentation work delivered directly by BIMForge Architecture.
          </p>
        </div>

        <div className="space-y-16">
          {/* Case Study 1: Spring St Onehunga */}
          <div className="grid lg:grid-cols-2 gap-12 items-start" data-testid="case-study-spring-st">
            <div>
              <Badge className="bg-[#10B981] text-white mb-4">Residential Subdivision</Badge>
              <h3 className="text-2xl font-bold mb-4">Spring Street, Onehunga: Reclaiming Functional Design</h3>
              <p className="text-[#A1A7B3] mb-6 leading-relaxed">
                I led this project as Owner, Designer, and Project Manager, transforming a single site into two distinct dwellings: a restored 1940s state house and a new contemporary two-story residence.
              </p>
              <p className="text-[#A1A7B3] mb-6 leading-relaxed">
                The project challenges recent development trends by prioritizing the features Aucklanders actually value. While standalone houses, garages, and off-street parking have become rare in new developments, we see them as essentials. I managed the entire lifecycle, from the first sketch to Resource and Building Consents, to prove that modern intensification can still deliver high-utility, standalone homes.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-[#10B981] font-medium">Scope:</span>
                  <span className="text-[#A1A7B3]">Full Resource Consent & Building Consent Documentation, Lot 1 & Lot 2</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-[#10B981] font-medium">Deliverables:</span>
                  <span className="text-[#A1A7B3]">Full design, detailing and project management service. Resource and building consent applications.</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-[#10B981] font-medium">Standards:</span>
                  <span className="text-[#A1A7B3]">NZS:3604, NZBC E2, B2 Compliance</span>
                </div>
              </div>
            </div>
            <div className="bg-[#14171F] border border-[#2C313D] p-4">
              <img
                src={springStImages[activeSpringImage]}
                alt="Spring Street Development"
                className="w-full h-64 object-cover mb-4"
              />
              <div className="grid grid-cols-4 gap-2">
                {springStImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveSpringImage(idx)}
                    className={`overflow-hidden border-2 transition-colors ${activeSpringImage === idx ? 'border-[#10B981]' : 'border-transparent'}`}
                  >
                    <img src={img} alt={`View ${idx + 1}`} className="w-full h-16 object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Case Study 2: JPPM Onehunga */}
          <div className="grid lg:grid-cols-2 gap-12 items-start" data-testid="case-study-jppm">
            <div className="lg:order-2">
              <Badge className="bg-[#10B981] text-white mb-4">Heritage Refurbishment & Extension</Badge>
              <h3 className="text-2xl font-bold mb-4">Onehunga Residential: Heritage Refurbishment & Extension</h3>
              <p className="text-[#A1A7B3] mb-6 leading-relaxed">
                This project involved the sensitive restoration and expansion of an original railway cottage in Onehunga, Auckland. In partnership with JP Project Management, our design focused on preserving the home's historical charm while integrating a high-spec modern extension. The architectural intervention significantly enhanced the home's market appeal, resulting in a highly competitive auction that drew record bidder numbers.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-[#10B981] font-medium">Location:</span>
                  <span className="text-[#A1A7B3]">Onehunga, Auckland</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-[#10B981] font-medium">Builder:</span>
                  <span className="text-[#A1A7B3]">JP Project Management (JPPM)</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-[#10B981] font-medium">Result:</span>
                  <span className="text-[#A1A7B3]">Record auction bidder numbers</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://jppm.co.nz/portfolio-item/onehunga/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="jppm-link"
                  className="btn-outline inline-flex items-center gap-2"
                >
                  <ArrowUpRight size={18} />
                  View on JPPM
                </a>
                <a
                  href="https://www.raywhite.co.nz/news-and-market-insights/news-media/41-registered-buyers-for-one-home-breaks-auckland-city-record"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nzherald-link"
                  className="btn-primary-green inline-flex items-center gap-2"
                >
                  <ArrowUpRight size={18} />
                  Read Auction Results
                </a>
              </div>
            </div>
            <div className="lg:order-1 bg-[#14171F] border border-[#2C313D] p-4">
              <img
                src={jppmImages[activeJppmImage]}
                alt="JPPM Onehunga Project"
                className="w-full h-72 object-cover mb-4"
              />
              <div className="grid grid-cols-6 gap-2">
                {jppmImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveJppmImage(idx)}
                    className={`overflow-hidden border-2 transition-colors ${activeJppmImage === idx ? 'border-[#10B981]' : 'border-transparent'}`}
                  >
                    <img src={img} alt={`View ${idx + 1}`} className="w-full h-12 object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Partners Section
const PartnersSection = () => {
  const partners = [
    {
      name: "JPPM",
      role: "Project Management & Building",
      description: "JP Project Management - Quality residential construction",
      link: "https://jppm.co.nz"
    },
    {
      name: "K Design",
      role: "Civil & Structural Engineers",
      description: "Civil and structural engineering expertise",
      link: "https://kdgroup.co.nz/"
    },
    {
      name: "Boundary Consultants",
      role: "Surveyors & Planners",
      description: "Land surveying, title plans, and planning services",
      link: "https://www.boundary.co.nz/"
    }
  ];

  return (
    <section id="partners" data-testid="partners-section" className="section section-dark">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#3B82F6] mb-4 block">
            Collaboration
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            People We Work With
          </h2>
          <p className="text-lg text-[#A1A7B3] leading-relaxed">
            We collaborate with trusted professionals to deliver complete project solutions.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {partners.map((partner, idx) => (
            <div
              key={idx}
              className="service-card service-card-blue text-center"
              data-testid={`partner-${idx}`}
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-[#1D212B] border border-[#3B82F6]/30 flex items-center justify-center text-[#3B82F6] font-bold text-xl">
                {partner.name.charAt(0)}
              </div>
              <h3 className="text-lg font-semibold mb-2">{partner.name}</h3>
              <p className="text-sm text-[#3B82F6] mb-2">{partner.role}</p>
              <p className="text-sm text-[#6E7687]">{partner.description}</p>
              {partner.link && (
                <a
                  href={partner.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-[#3B82F6] mt-3 hover:underline"
                >
                  Visit Website <ArrowUpRight size={14} />
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Skills Section
const SkillsSection = () => {
  const skills = [
    { name: "Revit", level: "Senior Level", icon: <Monitor size={20} /> },
    { name: "BIM Management", level: "Expert", icon: <Layers size={20} /> },
    { name: "Revizto", level: "Clash Detection & Training", icon: <Zap size={20} /> },
    { name: "Procore", level: "Project Management", icon: <Briefcase size={20} /> },
    { name: "Aconex", level: "Document Control", icon: <FileText size={20} /> },
    { name: "Drofus", level: "Data Management", icon: <Cpu size={20} /> },
  ];

  const specialties = [
    "Point Cloud to Revit Modeling",
    "Weather-tight Detailing",
    "Facade Coordination",
    "Advanced Revit Family Creation",
    "Twinmotion Visualization",
    "Construction Documentation",
  ];

  return (
    <section id="skills" data-testid="skills-section" className="section section-darker">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#3B82F6] mb-4 block">
              Technology
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-8">
              Skills & Expertise
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {skills.map((skill, idx) => (
                <div
                  key={idx}
                  className="tech-badge"
                  data-testid={`skill-${idx}`}
                >
                  {skill.icon}
                  <div>
                    <div className="font-medium text-white">{skill.name}</div>
                    <div className="text-xs text-[#6E7687]">{skill.level}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#10B981] mb-4 block">
              Specializations
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-8">
              Technical Focus
            </h2>
            <div className="space-y-3">
              {specialties.map((specialty, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 py-3 border-b border-[#2C313D]"
                  data-testid={`specialty-${idx}`}
                >
                  <ChevronRight size={16} className="text-[#10B981]" />
                  <span className="text-[#A1A7B3]">{specialty}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const credentials = [
    { icon: <Award size={20} />, text: "Licensed Building Practitioner (LBP Level 2)" },
    { icon: <FileText size={20} />, text: "National Diploma in Architectural Technology" },
    { icon: <Clock size={20} />, text: "23+ Years Construction Industry Experience" },
    { icon: <Building2 size={20} />, text: "10+ Years BIM/Revit Development" },
  ];

  const firms = ["Jasmax", "Klein Architects", "Ockham"];

  return (
    <section id="about" data-testid="about-section" className="section section-dark">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#A1A7B3] mb-4 block">
              About
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              Dustin Hogan
            </h2>
            <p className="text-xl text-[#3B82F6] font-medium mb-6">
              Senior Architectural Technician & BIM Manager
            </p>
            <p className="text-lg text-[#A1A7B3] leading-relaxed mb-8">
              A specialist consultancy bridging the gap between 3D marketing assets and real-world 2D construction documentation. Delivering production-ready BIM content and expert residential architectural services built on decades of hands-on experience.
            </p>
            <div className="space-y-4 mb-8">
              {credentials.map((cred, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-4"
                  data-testid={`credential-${idx}`}
                >
                  <div className="text-[#3B82F6]">{cred.icon}</div>
                  <span className="text-[#A1A7B3]">{cred.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#14171F] border border-[#2C313D] p-8">
            <h3 className="text-xl font-semibold mb-6">Experience with Leading NZ Firms</h3>
            <div className="space-y-4">
              {firms.map((firm, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-4 py-4 border-b border-[#2C313D] last:border-0"
                  data-testid={`firm-${idx}`}
                >
                  <div className="w-10 h-10 bg-[#1D212B] flex items-center justify-center text-[#3B82F6] font-bold">
                    {firm.charAt(0)}
                  </div>
                  <span className="text-lg">{firm}</span>
                </div>
              ))}
            </div>
            <Separator className="my-6 bg-[#2C313D]" />
            <p className="text-sm text-[#6E7687]">
              Combined experience across healthcare, residential, commercial, and retail sectors delivering BIM excellence throughout New Zealand.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" data-testid="contact-section" className="section section-darker">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#3B82F6] mb-4 block">
            Get in Touch
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Let's Build Something Together
          </h2>
          <p className="text-lg text-[#A1A7B3] leading-relaxed">
            Whether you need production-ready BIM content or expert architectural services, I'm here to help bring your project to life.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <a
            href="mailto:dustin@bimforge.co.nz"
            data-testid="contact-email"
            className="contact-link bg-[#14171F] border border-[#2C313D] hover:border-[#3B82F6] transition-colors"
          >
            <div className="icon-box-blue">
              <Mail size={20} />
            </div>
            <div>
              <div className="text-sm text-[#6E7687] mb-1">Email</div>
              <div className="font-medium">dustin@bimforge.co.nz</div>
            </div>
          </a>

          <a
            href="tel:021536169"
            data-testid="contact-phone"
            className="contact-link bg-[#14171F] border border-[#2C313D] hover:border-[#10B981] transition-colors"
          >
            <div className="icon-box-green">
              <Phone size={20} />
            </div>
            <div>
              <div className="text-sm text-[#6E7687] mb-1">Phone</div>
              <div className="font-medium">021 536 169</div>
            </div>
          </a>

          <div
            data-testid="contact-location"
            className="contact-link bg-[#14171F] border border-[#2C313D]"
          >
            <div className="icon-box-blue">
              <MapPin size={20} />
            </div>
            <div>
              <div className="text-sm text-[#6E7687] mb-1">Location</div>
              <div className="font-medium">Auckland, New Zealand</div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <a
            href="mailto:dustin@bimforge.co.nz"
            data-testid="contact-main-cta"
            className="btn-primary-blue inline-flex items-center gap-2 text-lg px-8 py-4"
          >
            <Mail size={20} />
            Send an Email
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="py-12 bg-[#0A0C10] border-t border-[#2C313D]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="BIMForge" className="h-8 w-auto" />
            <span className="text-[#6E7687] text-sm">
              Precision BIM & Architectural Excellence
            </span>
          </div>
          <div className="text-sm text-[#6E7687]">
            © {new Date().getFullYear()} BIMForge Ltd. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <div data-testid="home-page">
      <Navigation />
      <HeroSection />
      <StatsSection />
      <section id="services" className="divider" />
      <BIMForgeSection />
      <ArchitectureSection />
      <ExperienceSection />
      <CaseStudiesSection />
      <PartnersSection />
      <SkillsSection />
      <AboutSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App" data-testid="app-root">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
